import { appendContactAsk } from "./contactAsk";
import { appendSourceQuestion } from "./sourceQuestion";
import { aiDisclosureLine, deploymentShopDisplayName } from "./shopDisplayName";

/**
 * THE OUTBOUND TEXT REGISTRY. Item REG, rec 25b of the 10-03 decision list,
 * accepted under Kabir's typed reply "all recs i verify unelss it sacrifices
 * proffesionalims depolyoemnt, and system quality"; the rec, quoted whole:
 * "One registry of outbound text that the send path checks. Rec: yes, as a
 * later offline item." It builds on 25a (CLAUDE.md section 0.5): no automated
 * message may ever mention an offer.
 *
 * Every automated text that can reach a customer without a person typing it
 * for that customer is listed here once, by a stable id, with its exact text
 * (or every exact body a line built from parts can take), the context
 * `checkFixedLine` reads it under (its draft's goalStep), how it reaches a
 * customer and which files send it. A sender that sends a fixed text whole
 * names its id to `evaluateSendPath` (packages/agents sendPath.ts,
 * `registeredText`), and the send path refuses, with a restraint row and an
 * alert and nothing sent, an id that is not here, a body that is not the
 * registered text, and a registered text the send-time check fails.
 *
 * The texts are written here again on purpose, not imported from the senders.
 * Core is the one package every sender can import without a cycle (agents
 * and db both depend on it, and db cannot import agents), and a second,
 * reviewed copy is what makes it a registry rather than an alias: a sender
 * whose line drifts from this file is refused at send time and fails
 * packages/agents fixedLinesNoOffer.test.ts, which pins every shipped line to
 * its entry here byte for byte. Changing a line means changing it in both
 * places, and the second edit is the review.
 *
 * Three forms:
 *
 *  - `fixed`: the exact bodies. One for a single line; a line the code joins
 *    from registered parts lists every body the join can make.
 *  - `template`: a registered part the code adds to a body it did not write
 *    (the contact ask after a draft). The body is a draft and keeps a draft's
 *    gates; the template cannot vouch for a whole body, so the send path
 *    refuses it as a declared fixed text.
 *  - `by_kind`: campaign templates. Each is human-authored text read from the
 *    database and vouched for by its own approval row (item AP, migration
 *    0037) and `checkCampaignTemplate` at send time, never by a text here.
 *    The send path refuses it as a declared fixed text too.
 *
 * Not registered, because no customer reads them: the draft-waiting mail to
 * the Ackra ops inbox (packages/gateway snsDraftNoticeTool.ts, ids and enums
 * only), `T3_NOTE_LINE` (the owner's note-tier line) and every alert. A
 * model-composed reply is not a fixed text either: it keeps its own gates
 * (verifyDraft, the owner-edit gate, approval).
 *
 * Frozen, entries and lists alike, so nothing at runtime registers a text.
 */

/** How a registered text reaches a customer. */
export type OutboundTextRoute =
  /** Sent whole by a sender that names the id to `evaluateSendPath`. */
  | "send_path"
  /** Part of a draft the owner approves; verifyDraft and approval gate it. */
  | "draft_approval"
  /** Carried on an event row and sent by nothing today (F-06: nothing on that path transmits). */
  | "event_only";

/** Where a text's wording stands with Kabir. Not the marker phrase: the sender's doc comment carries that. */
export interface OutboundTextRatification {
  readonly status: "ratified" | "awaiting";
  /** The decision list item or FINDINGS entry that says so. */
  readonly ref: string;
}

interface EntryBase {
  /** Stable, dotted lower snake. A sender names it; never reuse one for new words. */
  readonly id: string;
  /** Repo-relative files that send or draft it. */
  readonly senders: readonly string[];
}

export interface FixedOutboundText extends EntryBase {
  readonly form: "fixed";
  /** Every exact body this id may go out as. */
  readonly renderings: readonly string[];
  /** The goalStep `checkFixedLine` reads it under, the one its sender drafts it with. */
  readonly context: string;
  readonly route: OutboundTextRoute;
  /** The exported constant the senders use, when one names it. */
  readonly constant?: string;
  readonly ratification: OutboundTextRatification;
}

export interface TemplateOutboundText extends EntryBase {
  readonly form: "template";
  /** The registered fixed part it adds. */
  readonly fills: string;
  readonly render: (body: string) => string;
  readonly route: OutboundTextRoute;
  readonly ratification: OutboundTextRatification;
}

export interface ByKindOutboundText extends EntryBase {
  readonly form: "by_kind";
  readonly context: string;
  /** What vouches for each text of this kind instead. */
  readonly vouchedBy: string;
}

export type OutboundTextEntry = FixedOutboundText | TemplateOutboundText | ByKindOutboundText;

// The parts the reply agent joins (replyAgent.ts, the `fixedLine` branch).
const HOLDING = "Checking on that for you, back shortly.";
// TA-1336: the disclosure names the deployment's shop (one deployment per
// shop, Kabir 2026-10-14), so its renderings are read when asked for, never
// fixed at load. For cl_nekta it is item 54's line, byte for byte.
const disclosure = (): string => aiDisclosureLine(deploymentShopDisplayName());
const CONTACT_ASK = "What's the best number or email to reach you on?";
const SOURCE_QUESTION = "Out of curiosity, where did you first come across it?";

function freeze<T extends OutboundTextEntry>(entry: T): T {
  // A getter (TA-1336, the shop's disclosure) hands out a fresh frozen array on every read.
  if (entry.form === "fixed" && Object.getOwnPropertyDescriptor(entry, "renderings")?.get === undefined) {
    Object.freeze(entry.renderings);
  }
  Object.freeze(entry.senders);
  if (entry.form !== "by_kind") Object.freeze(entry.ratification);
  return Object.freeze(entry);
}

export const OUTBOUND_TEXT_REGISTRY: readonly OutboundTextEntry[] = Object.freeze(
  [
    {
      id: "followup.check_in",
      form: "fixed",
      renderings: ["Wanted to check back in on this, happy to pick it up whenever works for you."],
      context: "followup_check_in",
      route: "send_path",
      senders: ["packages/agents/src/followUpSweep.ts"],
      constant: "CHECK_IN_NUDGE",
      ratification: { status: "awaiting", ref: "TA-181" },
    },
    {
      id: "review.ask",
      form: "fixed",
      renderings: ["Thanks for choosing us. If you have a moment, an honest review would mean a lot to us."],
      context: "review_ask",
      route: "send_path",
      senders: ["packages/agents/src/reviewSweep.ts"],
      constant: "REVIEW_ASK_MESSAGE",
      ratification: { status: "awaiting", ref: "TA-182, decision list item 11 of 10-03" },
    },
    {
      id: "review.public_recovery",
      form: "fixed",
      renderings: ["Thank you for telling us. We'd like to make this right, please message us directly."],
      context: "review_public_recovery",
      route: "draft_approval",
      senders: ["packages/agents/src/reviewCycleHandler.ts"],
      constant: "REVIEW_PUBLIC_RECOVERY_MESSAGE",
      ratification: { status: "awaiting", ref: "V4 REPUTATION, public response to a review below 4 stars" },
    },
    {
      id: "appointment.reminder_day_before",
      form: "fixed",
      renderings: ["Just a reminder, your appointment with us is coming up tomorrow. Let us know if anything's changed."],
      context: "appointment_reminder",
      route: "send_path",
      senders: ["packages/agents/src/appointmentReminderSweep.ts"],
      constant: "APPOINTMENT_REMINDER_DAY_BEFORE_MESSAGE",
      ratification: { status: "awaiting", ref: "TA-185" },
    },
    {
      id: "appointment.reminder_hour_before",
      form: "fixed",
      renderings: ["Quick reminder, your appointment with us is coming up shortly. See you soon."],
      context: "appointment_reminder",
      route: "send_path",
      senders: ["packages/agents/src/appointmentReminderSweep.ts"],
      constant: "APPOINTMENT_REMINDER_HOUR_BEFORE_MESSAGE",
      ratification: { status: "awaiting", ref: "TA-185" },
    },
    {
      id: "appointment.confirmation",
      form: "fixed",
      renderings: ["You're booked in, we look forward to seeing you."],
      context: "appointment_confirmation",
      route: "send_path",
      senders: ["packages/agents/src/consultBookHandler.ts"],
      constant: "APPOINTMENT_CONFIRMATION_MESSAGE",
      ratification: { status: "awaiting", ref: "V3 SCHEDULER, design open question 4" },
    },
    {
      id: "missed_call.text_back",
      form: "fixed",
      renderings: ["Hey, sorry we missed your call! Text us here anytime, happy to help."],
      context: "missed_call_text_back",
      route: "send_path",
      senders: ["packages/agents/src/missedCallTextBack.ts"],
      constant: "MISSED_CALL_TEXT_BACK_MESSAGE",
      ratification: { status: "awaiting", ref: "TA-188" },
    },
    {
      // Ratified for the no-answer draft (decision list items 78(a), 84(a));
      // its use when an SLA fires was not put to Kabir (TA-180).
      id: "escalation.holding",
      form: "fixed",
      renderings: [HOLDING],
      context: "escalation_holding",
      route: "send_path",
      senders: ["packages/agents/src/escalationSweep.ts"],
      constant: "HOLDING_MESSAGE",
      ratification: { status: "awaiting", ref: "TA-180 for the SLA use; items 78(a), 84(a) for the draft" },
    },
    {
      // A per-client override of the ack (`planOutageAck`'s plan) would be a
      // text not registered here, and the send path refuses it.
      id: "reply.outage_ack",
      form: "fixed",
      renderings: ["Got your message, back to you shortly."],
      context: "outage_ack",
      route: "send_path",
      senders: ["packages/agents/src/graph/composeHandler.ts"],
      constant: "OUTAGE_ACK",
      ratification: { status: "awaiting", ref: "TA-65, TA-142" },
    },
    {
      // Carried on the `reply.frozen` event (composeHandler.ts) and sent by
      // nothing today. A sender that starts sending it names this id.
      id: "reply.freeze_ack",
      form: "fixed",
      renderings: ["Thanks for reaching out, let me get someone to look at this for you."],
      context: "freeze_ack",
      route: "event_only",
      senders: ["packages/agents/src/graph/composeHandler.ts"],
      constant: "FREEZE_ACK",
      ratification: { status: "awaiting", ref: "TA-88, TA-142" },
    },
    {
      id: "reply.ai_disclosure",
      form: "fixed",
      get renderings(): readonly string[] {
        return Object.freeze([disclosure()]);
      },
      context: "escalate",
      route: "draft_approval",
      senders: ["packages/agents/src/replyAgent.ts"],
      constant: "AI_DISCLOSURE_LINE",
      ratification: { status: "ratified", ref: "decision list item 54(a)" },
    },
    {
      id: "contact_ask.no_answer",
      form: "fixed",
      renderings: [CONTACT_ASK],
      context: "escalate",
      route: "draft_approval",
      senders: ["packages/agents/src/replyAgent.ts", "packages/db/src/draftsCli.ts"],
      constant: "NO_ANSWER_CONTACT_ASK",
      ratification: { status: "ratified", ref: "decision list item 84(a)" },
    },
    {
      // TA-1336 C. The source question (where did you first come across it). Built OFF
      // (ACKRA_ENABLE_SOURCE_QUESTION); code appends it, a model never writes it.
      id: "source_question.ask",
      form: "fixed",
      renderings: [SOURCE_QUESTION],
      context: "answer",
      route: "draft_approval",
      senders: ["packages/agents/src/replyAgent.ts"],
      constant: "SOURCE_QUESTION_LINE",
      ratification: { status: "awaiting", ref: "TA-1336 C, wording proposed by the implementer from Kabir's 2026-10-14 brief" },
    },
    {
      // TA-1336 C. The question after a composed in-window reply, once per thread (replyAgent.ts).
      id: "source_question.appended",
      form: "template",
      fills: "source_question.ask",
      render: appendSourceQuestion,
      route: "draft_approval",
      senders: ["packages/agents/src/replyAgent.ts"],
      ratification: { status: "awaiting", ref: "TA-1336 C" },
    },
    {
      // The reply agent's fixed draft when it has nothing to compose from:
      // the disclosure when the customer asked if it is a bot, the holding
      // line, and the contact ask in grace. Every body the join can make.
      id: "reply.holding_draft",
      form: "fixed",
      get renderings(): readonly string[] {
        const d = disclosure();
        return Object.freeze([HOLDING, `${d} ${HOLDING}`, `${HOLDING} ${CONTACT_ASK}`, `${d} ${HOLDING} ${CONTACT_ASK}`]);
      },
      context: "escalate",
      route: "draft_approval",
      senders: ["packages/agents/src/replyAgent.ts"],
      ratification: { status: "ratified", ref: "decision list items 54(a), 78(a), 84(a), 103(b)" },
    },
    {
      // The ask after a composed reply in grace (replyAgent.ts) and at
      // approval with `--with-contact-ask` (draftsCli.ts), byte for byte
      // what the send path's own TA-498 check compares.
      id: "contact_ask.appended",
      form: "template",
      fills: "contact_ask.no_answer",
      render: appendContactAsk,
      route: "draft_approval",
      senders: ["packages/agents/src/replyAgent.ts", "packages/db/src/draftsCli.ts"],
      ratification: { status: "ratified", ref: "decision list item 102(a)" },
    },
    {
      // V5 CAMPAIGN. The public half of a comment-to-DM trigger: the private
      // reply opens the conversation (B5), this line points at it. DEFERRED
      // (V5 fix 1, B2): nothing writes it yet. It is to be drafted as a
      // `pending_approval` public reply, by a route that runs after that
      // comment's private reply is approved or sent; it states no figure,
      // date, price or offer word. TA-283: a private reply opens no messaging
      // window, only the customer's answer does.
      id: "comment.dm_pointer",
      form: "fixed",
      renderings: ["Sent you a message."],
      context: "comment_dm_pointer",
      route: "draft_approval",
      senders: ["packages/db/src/commentTriggers.ts"],
      constant: "COMMENT_DM_POINTER_LINE",
      ratification: { status: "awaiting", ref: "V5 CAMPAIGN, design section V5" },
    },
    {
      // X2 CATALOG. The text of a product insert in the composer rail: `<name>, <price>` and the product's
      // link, built by `composeProductInsert` (catalog.ts) from the catalog record, the price in integer
      // cents through the one money module. A template, not a fixed text: it is added to a draft the owner
      // approves, so the draft keeps every gate (verifier exact-matches the price against the versioned
      // product fact, the AI-tell linter, approval). It fills no registered line: `fills` names itself
      // because the catalog record, not a fixed text, is what it fills, and `render` is the identity on the
      // composed text. AWAITING RATIFICATION: the wording shape (name, price, link) is design, not a ruling.
      id: "catalog.product_insert",
      form: "template",
      fills: "catalog.product_insert",
      render: (text: string): string => text,
      route: "draft_approval",
      senders: ["packages/core/src/catalog.ts"],
      ratification: { status: "awaiting", ref: "X2 CATALOG, design section X2 and open question 9" },
    },
    {
      id: "campaign.template",
      form: "by_kind",
      context: "campaign_wave",
      senders: ["packages/agents/src/campaignSweep.ts"],
      vouchedBy: "its campaign_template_approvals row (item AP, migration 0037) and checkCampaignTemplate at send time",
    },
  ].map((e) => freeze(e as OutboundTextEntry)),
);

/** The entry an id names, or undefined. Never a default entry. */
export function registeredOutboundText(id: string): OutboundTextEntry | undefined {
  return OUTBOUND_TEXT_REGISTRY.find((e) => e.id === id);
}

export type RegisteredTextRefusal = "unregistered_text" | "not_a_fixed_text" | "text_mismatch";

/**
 * The send path's registry question, all of it but the check (`checkFixedLine`
 * lives in packages/agents, and the send path runs it on what comes back).
 * Byte for byte, on purpose: a fixed text has no cosmetic variants, and
 * whitespace or case that differs from the registered text is a different
 * text no review saw.
 */
export function matchRegisteredText(
  id: string,
  body: string,
): { readonly ok: true; readonly entry: FixedOutboundText } | { readonly ok: false; readonly reason: RegisteredTextRefusal } {
  const entry = registeredOutboundText(id);
  if (entry === undefined) return { ok: false, reason: "unregistered_text" };
  if (entry.form !== "fixed") return { ok: false, reason: "not_a_fixed_text" };
  if (!entry.renderings.includes(body)) return { ok: false, reason: "text_mismatch" };
  return { ok: true, entry };
}
